import { useState, useEffect, useCallback } from 'react'
import { sound } from '../utils/garden-sound.js'

// ── Design tokens (shared with practice page) ────────────────────────────────
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)', accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
  greenBg: 'var(--success-bg)', greenBorder: 'var(--success-border)',
  redBg: 'var(--error-bg)', redBorder: 'var(--error-border)',
  amberBg: 'var(--warn-bg)', amberBorder: 'var(--warn-border)',
  blue: 'var(--blue)', blueBg: 'var(--blue-bg)',
}
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
}

// ── Card shape & rating ──────────────────────────────────────────────────────
export type FlashcardItem = {
  id?: string
  front: string       // the prompt side (conjugated form / vocabulary term)
  back: string        // the answer side (translation / meaning)
  note?: string       // small uppercase label on the front face
  sub?: string        // optional secondary line under the back face
}
export type FlashRating = 'again' | 'hard' | 'good' | 'easy'

// ── Shared small components ──────────────────────────────────────────────────
export function Kbd({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: AF.m, fontSize: 10, color: C.muted,
      background: C.surf2, border: `1px solid ${C.border}`,
      borderBottomWidth: 2, padding: '1px 5px', borderRadius: 4,
      lineHeight: 1.5, userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</span>
  )
}

function CounterBadge({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{ fontFamily: AF.m, fontSize: 13, color: C.text, fontWeight: 600 }}>{current}</span>
      <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>/ {total}</span>
    </div>
  )
}

function ModeInstruction({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 24 }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>{text}</span>
    </div>
  )
}

function KbdRow({ hints }: { hints: [string, string][] }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 24px 16px', flexShrink: 0 }}>
      {hints.map(([key, label]) => (
        <span key={key + label} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <Kbd>{key}</Kbd>
          <span style={{ fontFamily: AF.b, fontSize: 11, color: C.dim }}>{label}</span>
        </span>
      ))}
    </div>
  )
}

function ScoreScreen({ score, total, onRetry, onExit, retryLabel = 'Try again', exitLabel = 'Next set →' }: {
  score: number; total: number; onRetry: () => void; onExit?: () => void; retryLabel?: string; exitLabel?: string
}) {
  const pct = total > 0 ? Math.round((score / total) * 100) : 100
  useEffect(() => { pct >= 80 ? sound.victory() : sound.wither() }, [])
  const color = pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--warn)' : 'var(--error)'
  const R = 48
  const C2PI = 2 * Math.PI * R
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px' }}>
        {total > 0 ? (
          <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 28px' }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r={R} fill="none" stroke={C.surf2} strokeWidth="7"/>
              <circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="7"
                strokeDasharray={C2PI}
                strokeDashoffset={C2PI * (1 - pct / 100)}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 1s ease 0.2s' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color, letterSpacing: '-1px', lineHeight: 1 }}>{pct}%</span>
              <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{score}/{total}</span>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 48, marginBottom: 20 }}>✓</div>
        )}
        <div style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: '-0.8px', marginBottom: 10 }}>
          {total === 0 ? 'Set complete.' : pct === 100 ? 'Perfect.' : pct >= 80 ? 'Well done.' : pct >= 50 ? 'Good effort.' : 'Keep at it.'}
        </div>
        <div style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, marginBottom: 36, lineHeight: 1.6 }}>
          {total > 0 ? `${score} of ${total} correct.` : `You reviewed all ${score} cards.`}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onRetry} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer' }}>
            {retryLabel}
          </button>
          <button onClick={onExit ?? onRetry} style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '12px 24px', borderRadius: 8, cursor: 'pointer' }}>
            {exitLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Flashcard game ───────────────────────────────────────────────────────────
export function FlashcardGame({
  cards, isMobile,
  icon = '🃏',
  scored = false,
  onRate,
  onExit,
  retryLabel,
  exitLabel,
}: {
  cards: FlashcardItem[]
  isMobile: boolean
  /** Emoji shown in the instruction badge above the card. */
  icon?: string
  /** When true, Good/Easy count as correct and the result screen shows a percentage. */
  scored?: boolean
  /** Called every time a card is rated — used to record SRS attempts. */
  onRate?: (rating: FlashRating, card: FlashcardItem) => void
  /** Optional exit handler for the secondary result-screen button. */
  onExit?: () => void
  retryLabel?: string
  exitLabel?: string
}) {
  const [idx, setIdx]                 = useState(0)
  const [flipped, setFlipped]         = useState(false)
  const [showRatings, setShowRatings] = useState(false)
  const [done, setDone]               = useState(false)
  const [reviewed, setReviewed]       = useState(0)
  const [correct, setCorrect]         = useState(0)
  const card = cards[idx]

  const flip = useCallback(() => {
    setFlipped(true)
    setTimeout(() => setShowRatings(true), 320)
  }, [])

  const advance = useCallback((rating: FlashRating) => {
    onRate?.(rating, card)
    if (scored && (rating === 'good' || rating === 'easy')) setCorrect(c => c + 1)
    setReviewed(r => r + 1)
    if (idx + 1 >= cards.length) { setDone(true); return }
    setFlipped(false); setShowRatings(false)
    setTimeout(() => setIdx(i => i + 1), 90)
  }, [idx, cards.length, card, scored, onRate])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.key === ' ' || e.key === 'ArrowRight') && !flipped) { e.preventDefault(); flip() }
      if (flipped && showRatings) {
        if (e.key === '1' || e.key === 'ArrowLeft') advance('again')
        if (e.key === '2') advance('hard')
        if (e.key === '3' || e.key === 'ArrowRight') advance('good')
        if (e.key === '4') advance('easy')
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [flipped, showRatings, flip, advance])

  const restart = () => { setIdx(0); setFlipped(false); setShowRatings(false); setDone(false); setReviewed(0); setCorrect(0) }

  if (done) return (
    <ScoreScreen
      score={scored ? correct : reviewed}
      total={scored ? cards.length : 0}
      onRetry={restart}
      onExit={onExit}
      retryLabel={retryLabel}
      exitLabel={exitLabel}
    />
  )

  const hp = isMobile ? '20px' : '64px'
  const CARD_H = isMobile ? 210 : 270

  const ratings: { id: FlashRating; label: string; hint: string; desc: string; color: string; bg: string; border: string }[] = [
    { id: 'again', label: 'Again', hint: '←', desc: "Didn't know", color: 'var(--error)',   bg: C.redBg,   border: C.redBorder   },
    { id: 'hard',  label: 'Hard',  hint: '2', desc: 'Struggled',   color: 'var(--warn)',    bg: C.amberBg, border: C.amberBorder  },
    { id: 'good',  label: 'Good',  hint: '→', desc: 'Got it',      color: 'var(--success)', bg: C.greenBg, border: C.greenBorder  },
    { id: 'easy',  label: 'Easy',  hint: '4', desc: 'Too easy',    color: C.blue,           bg: C.blueBg,  border: C.blueBg       },
  ]

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: `12px ${hp} 0` }}>
        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${(idx / cards.length) * 100}%`, transition: 'width 0.4s', borderRadius: 2 }}/>
        </div>
        <CounterBadge current={idx + 1} total={cards.length}/>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: `20px ${hp}` }}>
        <ModeInstruction icon={icon} text={flipped ? 'How well did you remember?' : 'Think of the meaning, then flip to check'}/>

        <div className="c8-flip-wrap" style={{ width: '100%', maxWidth: 480, height: CARD_H, marginBottom: 20, cursor: flipped ? 'default' : 'pointer' }}
          onClick={() => !flipped && flip()}>
          <div className={`c8-flip-card${flipped ? ' is-flipped' : ''}`}>
            <div className="c8-face" style={{
              borderRadius: 16, background: C.surf,
              boxShadow: `0 4px 28px rgba(0,0,0,0.22), 0 0 0 1px ${C.border}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: isMobile ? '28px 24px' : '40px 48px', textAlign: 'center',
            }}>
              {card.note && <div style={{ fontFamily: AF.m, fontSize: 11, color: C.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 18 }}>{card.note}</div>}
              <div style={{ fontFamily: AF.m, fontSize: isMobile ? 28 : 38, fontWeight: 500, color: C.text, marginBottom: 24, letterSpacing: '-0.5px', lineHeight: 1.2 }}>{card.front}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.dim }}>
                <span style={{ fontFamily: AF.b, fontSize: 12 }}>Tap to flip</span>
                <Kbd>Space</Kbd>
                <span style={{ fontFamily: AF.b, fontSize: 11, color: C.dim }}>or</span>
                <Kbd>→</Kbd>
              </div>
            </div>

            <div className="c8-face c8-face-back" style={{
              borderRadius: 16, background: C.surf2,
              boxShadow: `0 4px 28px rgba(0,0,0,0.22), 0 0 0 2px ${C.accentBorder}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: isMobile ? '28px 24px' : '40px 48px', textAlign: 'center',
            }}>
              <div style={{ fontFamily: AF.m, fontSize: 11, color: 'var(--accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 18 }}>Translation</div>
              <div style={{ fontFamily: AF.b, fontSize: isMobile ? 20 : 26, fontWeight: 600, color: C.text, lineHeight: 1.45, marginBottom: card.sub ? 10 : 0 }}>{card.back}</div>
              {card.sub && <div style={{ fontFamily: AF.m, fontSize: 13, color: C.muted }}>{card.sub}</div>}
            </div>
          </div>
        </div>

        {showRatings && (
          <div className="c8-pop" style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 480 }}>
            {ratings.map(({ id, label, hint, desc, color, bg, border }) => (
              <button key={id} onClick={() => advance(id)} style={{
                flex: 1, padding: '12px 6px 10px', borderRadius: 9,
                border: `1px solid ${border}`, background: bg, color,
                fontFamily: AF.b, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              }}>
                <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700 }}>{label}</span>
                <span style={{ fontSize: 10, opacity: 0.75 }}>{desc}</span>
                <Kbd>{hint}</Kbd>
              </button>
            ))}
          </div>
        )}
      </div>

      {!flipped
        ? <KbdRow hints={[['Space / →', 'flip card']]}/>
        : <KbdRow hints={[['1 / ←', 'Again'], ['2', 'Hard'], ['3 / →', 'Good'], ['4', 'Easy']]}/>
      }
    </div>
  )
}
